import React from 'react';
import { Form, Badge, Row, Col, Card, Alert } from 'react-bootstrap';
import {
    FaEdit,
    FaSave,
    FaTimes,
    FaUser,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBriefcase,
    FaFileAlt,
    FaImage,
    FaCheck,
    FaBan,
    FaComment,
    FaExclamationTriangle,
    FaCheckCircle,
    FaTimesCircle,
    FaUserTie,
    FaSyncAlt,
    FaExpand
} from 'react-icons/fa';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Swal from 'sweetalert2';
import useClientEOIDetail2 from './hooks/useClientEOIDetail2';

const ClientEOIDetailModal2 = ({ eoi, show, onClose, onUpdate }) => {
    const {
        isEditing,
        setIsEditing,
        comments,
        setComments,
        isLoading,
        isApproving,
        isRejecting,
        showRejectModal,
        rejectionReason,
        setRejectionReason,
        lightboxOpen,
        lightboxImages,
        lightboxIndex,
        currentUser,
        getStatusBadge,
        formatDate,
        formatDesiredStartDate,
        formatCurrency,
        getMediaUrl,
        prepareLightboxImages,
        handleSave,
        handleApprove,
        handleReject,
        openRejectModal,
        closeRejectModal,
        handleOpenLightbox,
        handleCloseLightbox,
        onCloseHandler
    } = useClientEOIDetail2({ eoi, show, onClose, onUpdate });

    if (!eoi || !show) return null;

    const professional = eoi.professional;
    const user = professional?.user;

    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié" }) => (
        <div className="display-field mb-3">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value bg-white rounded p-2">
                {value || emptyText}
            </div>
        </div>
    );

    return (
        <>
            {/* Modal principal */}
            <div
                className="eoi-detail2-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onCloseHandler}
            >
                <div
                    className="eoi-detail2-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                    <FaFileAlt className="text-primary" />
                                    Détails de la demande
                                </h4>
                                <div className="mt-2">
                                    {getStatusBadge(eoi.clientEOIStatus)}
                                </div>
                            </div>
                            <button className="btn-close" onClick={onCloseHandler}></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Informations du Client */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaUser className="me-2" />
                                Informations du Client
                            </h5>
                            <Row className="g-3">
                                <Col md={3} className="text-center">
                                    {eoi.client?.user?.profilePictureUrl ? (
                                        <img
                                            src={eoi.client.user.profilePictureUrl}
                                            alt="Profile"
                                            className="rounded-circle mb-2"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                objectFit: 'cover',
                                                border: '3px solid #28a745'
                                            }}
                                        />
                                    ) : (
                                        <div className="bg-white rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2"
                                             style={{ width: '100px', height: '100px', border: '3px solid #28a745' }}>
                                            <FaUser size={40} className="text-muted" />
                                        </div>
                                    )}
                                    <h6 className="text-success fw-bold">{eoi.client?.user?.username}</h6>
                                </Col>
                                <Col md={9}>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <DisplayField
                                                label="Nom complet"
                                                value={`${eoi.client?.user?.firstName} ${eoi.client?.user?.lastName}`}
                                                icon={<FaUser />}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <DisplayField
                                                label="Email"
                                                value={eoi.client?.user?.email || 'Non disponible'}
                                                icon={<FaEnvelope />}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <DisplayField
                                                label="Téléphone"
                                                value={eoi.client?.user?.phoneNumber || 'Non disponible'}
                                                icon={<FaPhone />}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <DisplayField
                                                label="Localisation"
                                                value={`${eoi.client?.user?.neighborhood ? eoi.client.user.neighborhood + ', ' : ''}${eoi.client?.user?.city || 'Ville non disponible'}, ${eoi.client?.user?.country || 'Pays non disponible'}`}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>

                        {/* Informations du professionnel */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaUserTie className="me-2" />
                                Informations du professionnel
                            </h5>
                            <Row className="g-3">
                                <Col md={3} className="text-center">
                                    {user?.profilePictureUrl ? (
                                        <img
                                            src={user.profilePictureUrl}
                                            alt="Profile"
                                            className="rounded-circle mb-2"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                objectFit: 'cover',
                                                border: '3px solid #0dcaf0'
                                            }}
                                        />
                                    ) : (
                                        <div className="bg-white rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2"
                                             style={{ width: '100px', height: '100px', border: '3px solid #0dcaf0' }}>
                                            <FaUserTie size={40} className="text-muted" />
                                        </div>
                                    )}
                                    <h6 className="text-info fw-bold">{user?.username}</h6>
                                    <Badge bg="info" className="rounded-pill mt-1">{professional?.selectedService}</Badge>
                                </Col>
                                <Col md={9}>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <DisplayField
                                                label="Nom complet"
                                                value={`${user?.firstName} ${user?.lastName}`}
                                                icon={<FaUser />}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <DisplayField
                                                label="Localisation"
                                                value={`${user?.neighborhood ? user.neighborhood + ', ' : ''}${user?.city}, ${user?.country}`}
                                                icon={<FaMapMarkerAlt />}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <DisplayField
                                                label="Expérience"
                                                value={`${professional?.yearsOfExperience || 0} ans`}
                                                icon={<FaSyncAlt />}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <DisplayField
                                                label="Service principal"
                                                value={eoi.serviceName || professional?.selectedService}
                                                icon={<FaBriefcase />}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>

                        {/* Détails de la demande */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">
                                <FaBriefcase className="me-2" />
                                Détails de la demande
                            </h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <DisplayField
                                        label="Localisation du service"
                                        value={eoi.serviceLocation || `${user?.city}, ${user?.country}`}
                                        icon={<FaMapMarkerAlt className="text-danger" />}
                                    />
                                </Col>
                                <Col md={6}>
                                    <DisplayField
                                        label="Date de début souhaitée"
                                        value={formatDesiredStartDate(eoi.desiredStartDate || professional?.desiredStartDate)}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                </Col>
                                <Col md={6}>
                                    <DisplayField
                                        label="Durée du contrat"
                                        value={`${eoi.contractDuration || 'Non spécifié'} mois`}
                                        icon={<FaCalendarAlt />}
                                    />
                                </Col>
                                {eoi.serviceFee && (
                                    <Col md={6}>
                                        <DisplayField
                                            label="Tarif du service"
                                            value={<span className="text-success fw-bold">{formatCurrency(eoi.serviceFee)}</span>}
                                            icon={<FaCheckCircle />}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </div>

                        {/* Commentaires */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaEdit className="me-2" />
                                Commentaires
                            </h5>
                            {isEditing ? (
                                <textarea
                                    rows={4}
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    placeholder="Ajoutez vos commentaires ou questions..."
                                    className="form-control"
                                    disabled={isLoading}
                                />
                            ) : (
                                <div className="field-value bg-white rounded p-3">
                                    {comments || eoi.comments || 'Aucun commentaire'}
                                </div>
                            )}
                            <small className="text-muted mt-2 d-block">Ce message sera visible par les parties concernées.</small>
                        </div>

                        {/* Galerie des médias avec Lightbox */}
                        {eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0 && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-warning">
                                    <FaImage className="me-2" />
                                    Galerie des médias
                                </h5>
                                <Row className="g-3">
                                    {eoi.mediaSubscriptions.map((media, index) => (
                                        <Col md={4} key={media.id || index}>
                                            <div
                                                className="rounded-3 overflow-hidden shadow-sm position-relative"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    const images = prepareLightboxImages(eoi);
                                                    handleOpenLightbox(images, index + 1); // +1 car l'image principale est en premier
                                                }}
                                            >
                                                <img
                                                    src={getMediaUrl(media)}
                                                    alt={media.name}
                                                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                                    className="img-fluid"
                                                    onError={(e) => {
                                                        e.target.src = "/assets/images/default-service.jpg";
                                                    }}
                                                />
                                                <div
                                                    className="position-absolute top-0 end-0 m-2 bg-dark bg-opacity-50 rounded-circle p-1"
                                                    style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <FaExpand size={14} color="white" />
                                                </div>
                                                <div className="bg-white p-2 text-center">
                                                    <small className="text-muted">{media.name}</small>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* Motif du rejet */}
                        {eoi.clientEOIStatus === 'REJECTED' && eoi.rejectionReason && (
                            <div className="bg-danger bg-opacity-10 rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-2 text-danger">
                                    <FaExclamationTriangle className="me-2" />
                                    Motif du rejet
                                </h5>
                                <div className="bg-white rounded p-3 border">
                                    {eoi.rejectionReason}
                                </div>
                            </div>
                        )}

                        {/* Informations de suivi */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-secondary">
                                <FaCalendarAlt className="me-2" />
                                Informations de suivi
                            </h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <DisplayField label="Date de création" value={formatDate(eoi.createdAt)} icon={<FaCalendarAlt />} />
                                    <DisplayField label="Créé par" value={eoi.createdBy || 'Non spécifié'} icon={<FaUser />} />
                                    {eoi.userValidated && (
                                        <DisplayField label="Validé par" value={eoi.userValidated} icon={<FaCheckCircle className="text-success" />} />
                                    )}
                                </Col>
                                <Col md={6}>
                                    <DisplayField label="Dernière modification" value={formatDate(eoi.modifiedAt) || 'Non modifié'} icon={<FaSyncAlt />} />
                                    <DisplayField label="Modifié par" value={eoi.lastModifiedBy || 'Non modifié'} icon={<FaUser />} />
                                    {eoi.userRejected && (
                                        <DisplayField label="Rejeté par" value={eoi.userRejected} icon={<FaTimesCircle className="text-danger" />} />
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Footer avec actions */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px', marginTop: 'auto'
                    }}>
                        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
                            <div className="d-flex gap-3">
                                {!isEditing ? (
                                    <button
                                        className="btn btn-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={onCloseHandler}
                                    >
                                        <FaTimes /> Fermer
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                            onClick={handleSave}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <span className="spinner-border spinner-border-sm" /> : <><FaSave /> Enregistrer</>}
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                            onClick={() => {
                                                setIsEditing(false);
                                                setComments(eoi.comments || '');
                                            }}
                                            disabled={isLoading}
                                        >
                                            <FaTimes /> Annuler
                                        </button>
                                    </>
                                )}
                            </div>

                            {eoi.clientEOIStatus === 'PENDING' && !isEditing && (
                                <div className="d-flex gap-3">
                                    <button
                                        className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={handleApprove}
                                        disabled={isApproving || isRejecting}
                                    >
                                        {isApproving ? <span className="spinner-border spinner-border-sm" /> : <><FaCheck /> Valider</>}
                                    </button>
                                    <button
                                        className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={openRejectModal}
                                        disabled={isRejecting || isApproving}
                                    >
                                        {isRejecting ? <span className="spinner-border spinner-border-sm" /> : <><FaBan /> Rejeter</>}
                                    </button>
                                </div>
                            )}

                            {eoi.clientEOIStatus === 'APPROVED' && !isEditing && (
                                <Alert variant="success" className="mb-0 rounded-pill">
                                    <FaCheckCircle className="me-2" /> Demande approuvée - Contrat créé
                                </Alert>
                            )}

                            {eoi.clientEOIStatus === 'REJECTED' && !isEditing && (
                                <Alert variant="danger" className="mb-0 rounded-pill">
                                    <FaTimesCircle className="me-2" /> Demande rejetée
                                </Alert>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de rejet */}
            {showRejectModal && (
                <div
                    className="reject-modal-overlay"
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1060, display: 'flex',
                        alignItems: 'center', justifyContent: 'center'
                    }}
                    onClick={closeRejectModal}
                >
                    <div
                        className="reject-modal-container"
                        style={{
                            backgroundColor: 'white', borderRadius: '24px',
                            width: '90%', maxWidth: '500px', animation: 'slideUp 0.3s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header-fixed" style={{
                            position: 'sticky', top: 0, backgroundColor: 'white',
                            borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '24px',
                            borderTopRightRadius: '24px', padding: '16px 24px'
                        }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-0 text-danger">
                                    <FaExclamationTriangle className="me-2" />
                                    Motif du rejet
                                </h5>
                                <button className="btn-close" onClick={closeRejectModal}></button>
                            </div>
                        </div>
                        <div className="p-4">
                            <Alert variant="warning" className="mb-4 rounded-3">
                                Vous êtes sur le point de rejeter cette demande. Veuillez indiquer le motif.
                            </Alert>
                            <div className="display-field mb-3">
                                <div className="field-label">Motif du rejet *</div>
                                <textarea
                                    rows={4}
                                    placeholder="Veuillez expliquer les raisons du rejet..."
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    className="form-control"
                                />
                                <small className="text-muted">Cette information sera visible par le client.</small>
                            </div>
                        </div>
                        <div className="modal-footer-fixed" style={{
                            position: 'sticky', bottom: 0, backgroundColor: 'white',
                            borderTop: '1px solid #e2e8f0', padding: '16px 24px'
                        }}>
                            <div className="d-flex justify-content-end gap-3">
                                <button className="btn btn-secondary rounded-pill px-4" onClick={closeRejectModal}>
                                    <FaTimes /> Annuler
                                </button>
                                <button
                                    className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2"
                                    onClick={handleReject}
                                    disabled={isRejecting || !rejectionReason.trim()}
                                >
                                    {isRejecting ? <span className="spinner-border spinner-border-sm" /> : <><FaBan /> Confirmer</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox pour les images */}
            <Lightbox
                open={lightboxOpen}
                close={handleCloseLightbox}
                slides={lightboxImages}
                index={lightboxIndex}
                styles={{
                    container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
                    button: { color: 'white' }
                }}
            />

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                .eoi-detail2-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }
                
                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }
                
                ::-webkit-scrollbar {
                    width: 6px;
                }
                
                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }
                
                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }
                
                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                
                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }
                
                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }
            `}</style>
        </>
    );
};

export default ClientEOIDetailModal2;